import React, { useState, useCallback, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, Legend, PieChart, Pie, LabelList
} from 'recharts';
import {
  Briefcase, FileText, AlertTriangle, Activity,
  Loader2, TrendingUp, Layers, Zap, CheckCircle, BarChart3
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format } from 'date-fns';

// --- Importa tus componentes locales ---
import { FilterBar } from './FilterBar';
import { api } from '../utils/apiClient';
import ReportBuilder from './ReportBuilder';

// --- CONFIGURACIÓN DE TEMA Legalplus "Midnight Court" ---
const THEME = {
  bg: "bg-brand-night",
  card: "bg-surface-1/70 border border-white/[0.06] backdrop-blur-xl shadow-brand-soft",
  textMain: "text-brand-silver",
  textMuted: "text-brand-silver-dim",
  gridStroke: "#17304D",
  tooltipBg: "#0A1628",
  // Paleta de series alineada a marca: indigo, cyan, amber, rose, violet, teal
  colors: ['#4F7DFF', '#5CE1E6', '#D4A85A', '#F15B5B', '#8B5CF6', '#34D399']
};

const SERVICE_COLORS_HEX = {
  'TELEFONICO':   '#4F7DFF',  // Indigo brand
  'SITIO':        '#34D399',  // Emerald
  'CONCILIACION': '#D4A85A',  // Amber brand
  'PENAL':        '#F15B5B',  // Danger
  'CIVIL':        '#8B5CF6',  // Violet
  'OTROS':        '#6B7A94',  // Silver-mute
  'DEFAULT':      '#5CE1E6',  // Cyan
  'Ninguna':      '#17304D'   // Navy-soft
};

// --- ERROR BOUNDARY ---
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('Error en gráfica:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full text-rose-400 bg-rose-900/20 rounded-xl p-4">
          <AlertTriangle className="mr-2" /> Error visual
        </div>
      );
    }
    return this.props.children;
  }
}

// --- SUB-COMPONENTES VISUALES ---

const HeaderModern = ({ onGenerateReport, isLoading, userRole, onMisReportes }) => (
  <div className="flex flex-col md:flex-row justify-between items-end mb-8 pb-6 border-b border-white/[0.06]">
    <div>
      <div className="flex items-center gap-2.5 mb-3">
        <span className="inline-block w-8 h-px bg-brand-indigo" />
        <span className="eyebrow text-brand-indigo-soft">Command center</span>
      </div>
      <h1 className="font-display font-bold text-4xl tracking-[-0.025em] text-brand-silver leading-[1.05]">
        Vision
        <span className="ml-2 bg-gradient-to-r from-brand-indigo-soft via-brand-indigo to-brand-cyan bg-clip-text text-transparent">
          LegalPlus
        </span>
      </h1>
      <p className="mt-2.5 text-sm text-brand-silver-dim leading-relaxed max-w-md">
        Inteligencia legal y métricas operativas en tiempo real — firmada por su auditoría.
      </p>
    </div>
    <div className="flex items-center gap-3 mt-4 md:mt-0">
      {['administrador', 'contabilidad'].includes((userRole || '').toLowerCase()) && (
        <button
          onClick={onMisReportes}
          className="group relative rounded-brand border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-brand-amber/40 text-brand-silver font-medium py-2.5 px-5 text-sm transition-all duration-[280ms] ease-brand flex items-center gap-2"
        >
          <BarChart3 size={16} className="text-brand-amber" />
          Mis Reportes
        </button>
      )}
      <button
        onClick={onGenerateReport}
        disabled={isLoading}
        className="group relative overflow-hidden rounded-brand text-white font-semibold py-2.5 px-5 text-sm transition-all duration-[280ms] ease-brand disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 shadow-brand-lift active:scale-[0.98]"
        style={{ background: 'linear-gradient(135deg, #4F7DFF 0%, #2B4EB8 100%)' }}
      >
        <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%)' }} />
        <span className="relative flex items-center gap-2">
          {isLoading ? <Loader2 className="animate-spin" size={16}/> : <FileText size={16}/>}
          {isLoading ? 'Generando…' : 'Exportar PDF'}
        </span>
      </button>
    </div>
  </div>
);

const ModernStatCard = ({ title, value, icon: Icon, colorIdx, subtext }) => {
  // Paleta Legalplus — indigo, cyan, amber, violet (rotada)
  const accents = [
    { bg: 'bg-brand-indigo/10',  ring: 'ring-brand-indigo/25',  text: 'text-brand-indigo-soft', glow: 'rgba(79,125,255,0.22)' },
    { bg: 'bg-brand-cyan/10',    ring: 'ring-brand-cyan/25',    text: 'text-brand-cyan',        glow: 'rgba(92,225,230,0.22)' },
    { bg: 'bg-brand-amber/10',   ring: 'ring-brand-amber/25',   text: 'text-brand-amber',       glow: 'rgba(212,168,90,0.22)' },
    { bg: 'bg-violet-500/10',    ring: 'ring-violet-400/25',    text: 'text-violet-300',        glow: 'rgba(139,92,246,0.22)' },
  ];
  const a = accents[colorIdx % accents.length];

  return (
    <div
      className={`relative overflow-hidden rounded-brand-lg ${THEME.card} p-6 group transition-all duration-[400ms] ease-brand hover:-translate-y-[2px] hover:border-white/[0.1]`}
      style={{ '--card-glow': a.glow }}
    >
      {/* Sheen highlight top */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      {/* Icon ghost background */}
      <Icon
        size={140}
        className={`absolute -top-6 -right-6 ${a.text} opacity-[0.05] group-hover:opacity-[0.09] transition-opacity duration-[400ms]`}
        strokeWidth={1.5}
      />

      {/* Hover glow */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-[500ms] pointer-events-none"
        style={{ background: `radial-gradient(circle at 100% 0%, ${a.glow} 0%, transparent 60%)` }}
      />

      <div className="relative z-10">
        <div className="flex items-center gap-2.5 mb-5">
          <div className={`p-2 rounded-brand ${a.bg} ring-1 ${a.ring} ${a.text}`}>
            <Icon size={16} strokeWidth={2.2} />
          </div>
          <h3 className="eyebrow text-brand-silver-mute">{title}</h3>
        </div>

        <p className="font-display font-bold text-[2.5rem] leading-[1] tracking-[-0.03em] text-brand-silver tabular-nums">
          {value}
        </p>

        {subtext && (
          <p className="mt-3 text-[11px] font-medium text-brand-silver-mute leading-relaxed">
            {subtext}
          </p>
        )}
      </div>
    </div>
  );
};

const ModernAreaChart = ({ allData, isDailyView, windowStart, onPrev, onNext }) => {
  const gradients = [
    { id: 'color0', color: '#3b82f6' },
    { id: 'color1', color: '#10b981' },
    { id: 'color2', color: '#f59e0b' },
    { id: 'color3', color: '#ec4899' },
    { id: 'color4', color: '#8b5cf6' },
  ];

  const WINDOW_SIZE = 14;
  const visibleData = isDailyView ? allData.slice(windowStart, windowStart + WINDOW_SIZE) : allData;
  const keys = allData.length > 0 ? Object.keys(allData[0]).filter(k => k !== 'name' && k !== 'periodo') : [];
  const canPrev = isDailyView && windowStart > 0;
  const canNext = isDailyView && windowStart + WINDOW_SIZE < allData.length;
  const totalDays = allData.length;

  return (
    <div id="flujo-casos-chart" className={`w-full h-[400px] ${THEME.card} rounded-3xl p-6 flex flex-col transition-colors`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Activity className="text-blue-500" size={20} />
          {isDailyView ? 'Dinámica Diaria' : 'Tendencia Mensual'}
        </h3>
        {isDailyView && totalDays > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={onPrev}
              disabled={!canPrev}
              className="px-2.5 py-1 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ← Ant
            </button>
            <span className="text-xs text-slate-400 min-w-[90px] text-center">
              {windowStart + 1}–{Math.min(windowStart + WINDOW_SIZE, totalDays)} / {totalDays} días
            </span>
            <button
              onClick={onNext}
              disabled={!canNext}
              className="px-2.5 py-1 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Sig →
            </button>
          </div>
        )}
      </div>

      <div className="flex-grow w-full h-full min-h-0">
        <ChartErrorBoundary>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={visibleData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                {gradients.map((g) => (
                  <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={g.color} stopOpacity={0.5}/>
                    <stop offset="95%" stopColor={g.color} stopOpacity={0}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={THEME.gridStroke} vertical={false} opacity={0.4} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 11}} dy={10} interval={0} />
              <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 11}} />
              <Tooltip contentStyle={{ backgroundColor: THEME.tooltipBg, borderColor: THEME.gridStroke, borderRadius: '12px', color: '#fff' }} itemStyle={{ color: '#e2e8f0' }} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle"/>
              {keys.map((key, index) => {
                const grad = gradients[index % gradients.length];
                return (
                  <Area key={key} type="monotone" dataKey={key} stroke={grad.color} fill={`url(#${grad.id})`} strokeWidth={3} fillOpacity={1} />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </ChartErrorBoundary>
      </div>
    </div>
  );
};

const ModernBarChart = ({ data }) => (
  <div id="asistencias-tipo-chart" className={`w-full h-[400px] ${THEME.card} rounded-3xl p-6 flex flex-col transition-colors`}>
    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
      <Layers className="text-emerald-500" size={20} />
      Servicios
    </h3>
    <div className="flex-grow w-full h-full min-h-0">
      <ChartErrorBoundary>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 40, left: 40, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={THEME.gridStroke} opacity={0.4}/>
            <XAxis type="number" hide />
            <YAxis dataKey="name" type="category" width={100} tick={{fill: '#e2e8f0', fontSize: 11, fontWeight: 500}} axisLine={false} tickLine={false} />
            <Tooltip cursor={{fill: 'rgba(255,255,255,0.05)'}} contentStyle={{ backgroundColor: THEME.tooltipBg, borderColor: THEME.gridStroke, borderRadius: '12px', color: '#fff' }} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
              <LabelList dataKey="value" position="right" style={{ fill: '#fff', fontSize: '11px', fontWeight: 'bold' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartErrorBoundary>
    </div>
  </div>
);

const ModernRankingList = ({ title, icon: Icon, data, iconColor = "text-blue-500" }) => {
  const max = data.length > 0 ? Math.max(...data.map(d => d.value)) : 1;
  const rankColors = ['text-amber-400', 'text-slate-300', 'text-amber-700'];

  return (
    <div className={`w-full h-[400px] ${THEME.card} rounded-3xl p-6 flex flex-col transition-colors`}>
      <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
        <Icon className={iconColor} size={20} />
        {title}
      </h3>
      <div className="flex-grow overflow-y-auto space-y-3.5 pr-1 custom-scrollbar">
        {data.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-500 text-sm">Sin datos</div>
        ) : data.map((item, index) => {
          const pct = Math.round((item.value / max) * 100);
          return (
            <div key={index}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`text-xs font-black w-5 shrink-0 text-center ${rankColors[index] ?? 'text-slate-500'}`}>
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium text-slate-200 truncate">{item.name}</span>
                </div>
                <span className="text-sm font-bold text-white ml-3 shrink-0 tabular-nums">{item.value.toLocaleString()}</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden ml-7">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, backgroundColor: item.color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ModernHourlyActivity = ({ fullData }) => {
  const maxValue = Math.max(...fullData.map(d => d.total || 0), 1); 

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const activeItems = payload.filter(p => p.value > 0).reverse();
      const total = activeItems.reduce((acc, curr) => acc + curr.value, 0);
      return (
        <div className="bg-slate-900/95 border border-slate-700 p-3 rounded-xl shadow-xl backdrop-blur-md min-w-[140px]">
          <p className="text-slate-400 text-xs font-bold mb-2 pb-1 border-b border-slate-700">{label} Horas</p>
          <div className="space-y-1">
             {activeItems.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center text-xs">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.fill }}></div>
                        <span className="text-slate-300 capitalize">{item.name.toLowerCase()}</span>
                    </div>
                    <span className="font-bold text-white">{item.value}</span>
                </div>
             ))}
          </div>
          <div className="mt-2 pt-1 border-t border-slate-700 flex justify-between text-xs">
             <span className="text-slate-500">Total</span>
             <span className="font-bold text-white">{total}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const stackKeys = ['TELEFONICO', 'SITIO', 'CONCILIACION', 'PENAL', 'CIVIL', 'OTROS', 'DEFAULT'];

  return (
    <div id="asistencia-heatmap" className={`w-full ${THEME.card} rounded-3xl p-6 flex flex-col transition-colors`}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Zap className="text-amber-400" size={20} />
            Ritmo de Actividad
          </h3>
          <p className="text-xs text-slate-400/80 dark:text-slate-500 mt-1">Volumen por hora (Desglose)</p>
        </div>
        <div className="hidden sm:flex items-center gap-2 bg-slate-950/50 px-3 py-1.5 rounded-lg border border-slate-800">
           <Activity size={14} className="text-emerald-400" />
           <span className="text-xs text-slate-300">Pico: <strong className="text-white">{maxValue}</strong></span>
        </div>
      </div>

      <div className="h-64 w-full">
        <ChartErrorBoundary>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={fullData} margin={{ top: 20, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={THEME.gridStroke} opacity={0.3} />
              <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} interval={1} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
              {stackKeys.map((key) => (
                  <Bar 
                    key={key} 
                    dataKey={key} 
                    stackId="a" 
                    fill={SERVICE_COLORS_HEX[key] || SERVICE_COLORS_HEX.DEFAULT}
                    animationDuration={1000}
                  >
                      <LabelList 
                        dataKey={key} 
                        position="inside" 
                        style={{ fill: 'white', fontSize: '9px', fontWeight: 'bold', textShadow: '0px 0px 2px black' }} 
                        formatter={(val) => val > 0 ? val : ''} 
                      />
                  </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartErrorBoundary>
      </div>
      
      <div className="flex flex-wrap gap-4 mt-6 justify-center border-t border-slate-800/50 pt-4">
          {Object.entries(SERVICE_COLORS_HEX).map(([label, color]) => {
            if (label === 'DEFAULT' || label === 'Ninguna' || label === 'OTROS') return null;
            return (
              <div key={label} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }}></span>
                <span className="text-[10px] uppercase font-bold text-slate-400">{label}</span>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const CaseStatusDonut = ({ active, total }) => {
  const closed = Math.max(0, total - active);
  const resolution = total > 0 ? ((closed / total) * 100).toFixed(0) : '0';
  const data = [
    { name: 'Activos', value: active, color: '#3b82f6' },
    { name: 'Cerrados', value: closed, color: '#10b981' },
  ];

  return (
    <div className={`w-full h-[400px] ${THEME.card} rounded-3xl p-6 flex flex-col items-center justify-center transition-colors`}>
      <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2 w-full text-left">
        <CheckCircle className="text-purple-500" size={20} />
        Ratio de Resolución
      </h3>
      <div className="flex-grow w-full h-full min-h-0 relative">
        <ChartErrorBoundary>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                stroke="none"
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`} 
                labelLine={{ stroke: '#475569' }}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: THEME.tooltipBg, borderColor: THEME.gridStroke, borderRadius: '12px', color: '#fff' }} />
              <Legend verticalAlign="bottom" height={36}/>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-8">
              <span className="text-3xl font-bold text-white">{resolution}%</span>
              <span className="text-xs text-slate-400 uppercase">Resueltos</span>
          </div>
        </ChartErrorBoundary>
      </div>
    </div>
  );
};

const JudicialSummaryCard = ({ data }) => {
  if (!data) return null;

  const total        = data.total_judiciales ?? 0;
  const gestionados  = data.gestionados      ?? 0;
  const pendientes   = data.pendientes       ?? 0;
  const audiencia    = data.audiencia        ?? 0;
  const seguimiento  = data.seguimiento      ?? 0;
  const conSesion    = data.con_fecha_sesion ?? 0;

  const tasaGestion  = total > 0 ? Math.round((gestionados / total) * 100) : 0;
  const R            = 36;
  const circumference = 2 * Math.PI * R;
  const strokeOffset  = circumference - (tasaGestion / 100) * circumference;

  const metrics = [
    { label: 'Gestionados',   value: gestionados, color: '#10b981', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle },
    { label: 'Pendientes',    value: pendientes,  color: '#f59e0b', bg: 'bg-amber-500/10',   border: 'border-amber-500/20',   text: 'text-amber-400',   icon: AlertTriangle },
    { label: 'En Audiencia',  value: audiencia,   color: '#3b82f6', bg: 'bg-blue-500/10',    border: 'border-blue-500/20',    text: 'text-blue-400',    icon: Briefcase },
    { label: 'Seguimiento',   value: seguimiento, color: '#8b5cf6', bg: 'bg-violet-500/10',  border: 'border-violet-500/20',  text: 'text-violet-400',  icon: Activity },
    { label: 'Con Sesion',    value: conSesion,   color: '#06b6d4', bg: 'bg-cyan-500/10',    border: 'border-cyan-500/20',    text: 'text-cyan-400',    icon: Layers },
  ];

  return (
    <div className={`w-full ${THEME.card} rounded-3xl p-6 transition-colors`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Layers className="text-blue-400" size={20} />
            Resumen Judicial
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Seguimiento de procesos activos</p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-black text-white">{total.toLocaleString()}</p>
          <p className="text-xs text-slate-500">procesos totales</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        {/* Gauge circular de tasa de gestión */}
        <div className="flex flex-col items-center justify-center shrink-0 mx-auto md:mx-0">
          <div className="relative w-32 h-32">
            <svg viewBox="0 0 84 84" className="w-full h-full -rotate-90">
              <circle cx="42" cy="42" r={R} fill="none" stroke="#1e293b" strokeWidth="8" />
              <circle
                cx="42" cy="42" r={R}
                fill="none"
                stroke="#10b981"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeOffset}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-black text-white leading-none">{tasaGestion}%</span>
              <span className="text-[10px] text-slate-400 mt-0.5">gestion</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center leading-tight">Tasa de<br/>resolucion</p>
        </div>

        {/* Grid de metricas */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 flex-grow w-full">
          {metrics.map((item, idx) => {
            const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
            const Icon = item.icon;
            return (
              <div key={idx} className={`rounded-2xl ${item.bg} border ${item.border} p-3.5`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Icon size={13} className={item.text} />
                  <span className="text-[11px] text-slate-400 font-medium leading-none">{item.label}</span>
                </div>
                <p className={`text-2xl font-black ${item.text} leading-none`}>{item.value.toLocaleString()}</p>
                <div className="mt-2.5 h-1 rounded-full bg-slate-800/60">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: item.color }} />
                </div>
                <p className="text-[10px] text-slate-600 mt-1">{pct}% del total</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// --- LOGICA GENERADORES URL ---
const buildQueryStringMain = (filters) => {
  const params = new URLSearchParams();

  if (filters.cliente) params.append('cliente_corporativo', filters.cliente);
  if (filters.abogado) params.append('abogado', filters.abogado);
  if (filters.tipoServicio) params.append('tarifa', filters.tipoServicio);
  if (filters.aseguradora) params.append('aseguradora', filters.aseguradora);

  if (filters.fechaInicio) {
    params.append('fecha_inicio', format(filters.fechaInicio, 'yyyy-MM-dd'));
  }
  if (filters.fechaFin) {
    params.append('fecha_fin', format(filters.fechaFin, 'yyyy-MM-dd'));
  }

  if (!filters.fechaInicio && !filters.fechaFin) {
    if (filters.dia && filters.mes && filters.anio) {
      const formattedMes = String(filters.mes).padStart(2, '0');
      const formattedDia = String(filters.dia).padStart(2, '0');
      params.append('fecha', `${filters.anio}-${formattedMes}-${formattedDia}`);
    } else if (filters.mes && filters.anio) {
      const formattedMes = String(filters.mes).padStart(2, '0');
      params.append('fecha', `${filters.anio}-${formattedMes}`);
    } else if (filters.anio) {
      params.append('fecha', filters.anio);
    }
  }

  return params.toString() ? `?${params.toString()}` : '';
};

const buildQueryStringFlujo = (filters) => {
  const params = new URLSearchParams();

  if (filters.cliente) params.append('cliente_corporativo', filters.cliente);
  if (filters.aseguradora) params.append('aseguradora', filters.aseguradora);

  if (filters.fechaInicio) {
    params.append('fecha_inicio', format(filters.fechaInicio, 'yyyy-MM-dd'));
  }
  if (filters.fechaFin) {
    params.append('fecha_fin', format(filters.fechaFin, 'yyyy-MM-dd'));
  }

  if (!filters.fechaInicio && !filters.fechaFin) {
    if (filters.dia && filters.mes && filters.anio) {
      const formattedMes = String(filters.mes).padStart(2, '0');
      const formattedDia = String(filters.dia).padStart(2, '0');
      params.append('fecha', `${filters.anio}-${formattedMes}-${formattedDia}`);
    } else if (filters.mes && filters.anio) {
      const formattedMes = String(filters.mes).padStart(2, '0');
      params.append('fecha', `${filters.anio}-${formattedMes}`);
    } else if (filters.anio) {
      params.append('fecha', filters.anio);
    }
  }

  return params.toString() ? `?${params.toString()}` : '';
};


export default function Dashboard({ user }) {
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const now = new Date();
  const initialMonth = String(now.getMonth() + 1);
  const initialYear = String(now.getFullYear());

  const [filters, setFilters] = useState({
    cliente: '',
    abogado: '',
    fechaInicio: null,
    fechaFin: null,
    tipoServicio: '',
    aseguradora: 'TODAS',
    dia: '',
    mes: initialMonth,
    anio: initialYear,
  });

  const [cardsData, setCardsData] = useState([]);
  const [flujoCasosData, setFlujoCasosData] = useState([]);
  const [flujoCasosKeys, setFlujoCasosKeys] = useState([]);
  const [windowStart, setWindowStart] = useState(0);
  const [asistenciasPorTipoData, setAsistenciasPorTipoData] = useState([]);
  const [caseMetrics, setCaseMetrics] = useState({ active: 0, total: 0 });
  const [asistenciaHorariaData, setAsistenciaHorariaData] = useState(Array.from({ length: 24 }, (_, i) => ({ hour: `${i}:00`, total: 0 })));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vistaActual, setVistaActual] = useState('dashboard');
  const [topAbogadosData, setTopAbogadosData] = useState([]);
  const [topClientesData, setTopClientesData] = useState([]);
  const [judicialResumen, setJudicialResumen] = useState(null);
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true); setError(null);
      const queryParamsMain = buildQueryStringMain(filters);
      const queryParamsFlujo = buildQueryStringFlujo(filters);
      try {
        // const [
        //   d_casosActivos,
        //   d_asistenciasTotales,
        //   d_casosTotales,
        //   d_asistenciasTipo,
        //   d_asistenciasHora,
        // ] = await Promise.all([
        //   api.get(`/dashboard/casos_activos${queryParamsMain}`),
        //   api.get(`/dashboard/asistencias_totales${queryParamsMain}`),
        //   api.get(`/dashboard/casos_totales${queryParamsMain}`),
        //   api.get(`/dashboard/asistencias_por_tipo${queryParamsMain}`),
        //   api.get(`/dashboard/asistencias_por_hora${queryParamsMain}`),
        // ]);

        const [
            d_resumenOperativo,
            d_asistenciasTipo,
            d_asistenciasHora,
            d_topAbogados,
            d_topClientes,
            d_judicialResumen,
          ] = await Promise.all([
            api.get(`/dashboard/resumen_operativo${queryParamsMain}`),
            api.get(`/dashboard/asistencias_por_tipo${queryParamsMain}`),
            api.get(`/dashboard/asistencias_por_hora${queryParamsMain}`),
            api.get(`/dashboard/top_abogados${queryParamsMain}`),
            api.get(`/dashboard/top_clientes${queryParamsMain}`),
            api.get(`/dashboard/judicial_resumen`),
          ]);
        let d_flujoCasos = [];
        try { d_flujoCasos = await api.get(`/dashboard/flujo_casos_mensual${queryParamsFlujo}`); } catch (err) {}

        const valActivos = parseInt(d_resumenOperativo.casos_activos || 0);
        const valTotales = parseInt(d_resumenOperativo.casos_totales || 0);
        const valAsistencias = parseInt(d_resumenOperativo.asistencias_totales || 0);
        const valCerrados = parseInt(d_resumenOperativo.casos_cerrados || 0);
        const valResolucion = Number(d_resumenOperativo.tasa_resolucion || 0);
        const valPromedio = Number(d_resumenOperativo.promedio_asistencias_por_caso || 0);

        setCaseMetrics({ active: valActivos, total: valTotales });

        setCardsData([
          {
            title: 'Casos Activos',
            value: valActivos.toString(),
            icon: Briefcase,
            subtext: 'En proceso actual'
          },
          {
            title: 'Asistencias',
            value: valAsistencias.toString(),
            icon: Zap,
            subtext: `${valPromedio} por caso`
          },
          {
            title: 'Expedientes',
            value: valTotales.toString(),
            icon: FileText,
            subtext: `${valCerrados} cerrados`
          },
          {
            title: 'Resolución',
            value: `${valResolucion}%`,
            icon: Activity,
            subtext: 'Tasa operativa'
          }
        ]);

        const isDailyFetch = !!(filters.fechaInicio || filters.fechaFin || (filters.mes && filters.anio));
        const normalizeKey = (k) => {
          const lower = k.toLowerCase();
          if (lower.includes('asis') && lower.includes('prex')) return 'Assisprex';
          return k;
        };
        const rawKeys = d_flujoCasos.length > 0 ? Object.keys(d_flujoCasos[0]).filter(k => k !== 'periodo' && k !== 'total_general' && k !== 'name') : [];
        const keys = [...new Set(rawKeys.map(normalizeKey))];
        // Renombrar claves en los datos si alguna fue normalizada
        const normalizeFlujoEntry = (entry) => {
          const result = {};
          Object.keys(entry).forEach(k => { result[normalizeKey(k)] = entry[k]; });
          return result;
        };
        const d_flujoCasosNorm = d_flujoCasos.map(normalizeFlujoEntry);
        setFlujoCasosKeys(keys);

        let formattedFlujo = [];
        if (isDailyFetch) {
          let startDate, endDate;
          if (filters.fechaInicio || filters.fechaFin) {
            startDate = filters.fechaInicio ? new Date(filters.fechaInicio) : new Date(filters.fechaFin);
            endDate = filters.fechaFin ? new Date(filters.fechaFin) : new Date();
          } else {
            const yr = parseInt(filters.anio);
            const mo = parseInt(filters.mes);
            startDate = new Date(yr, mo - 1, 1);
            endDate = new Date(yr, mo, 0);
          }
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          const cur = new Date(startDate);
          while (cur <= endDate) {
            const yr = cur.getFullYear();
            const mo = String(cur.getMonth() + 1).padStart(2, '0');
            const dy = String(cur.getDate()).padStart(2, '0');
            const dStr = `${yr}-${mo}-${dy}`;
            const entry = d_flujoCasosNorm.find(e => e.periodo === dStr);
            const obj = { name: `${dy}/${mo}`, periodo: dStr };
            keys.forEach(k => obj[k] = entry ? (entry[k] || 0) : 0);
            formattedFlujo.push(obj);
            cur.setDate(cur.getDate() + 1);
          }
          const newWindow = Math.max(0, formattedFlujo.length - 14);
          setWindowStart(newWindow);
        } else {
          const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
          formattedFlujo = monthNames.map((name, i) => {
            const mKey = String(i + 1).padStart(2, '0');
            const entry = d_flujoCasosNorm.find(d => d.periodo && d.periodo.includes(`-${mKey}-`));
            const obj = { name };
            keys.forEach(k => obj[k] = entry ? entry[k] : 0);
            return obj;
          });
          setWindowStart(0);
        }
        setFlujoCasosData(formattedFlujo);

        const formattedAsistencias = d_asistenciasTipo.filter(d => d.tipo_servicio !== 'OTROS').map((d, index) => ({
          name: d.tipo_servicio, value: d.total, color: THEME.colors[index % THEME.colors.length],
        }));
        setAsistenciasPorTipoData(formattedAsistencias);
        setTopAbogadosData(
          (d_topAbogados || []).map((d, index) => ({
            name: d.abogado,
            value: d.total,
            color: THEME.colors[index % THEME.colors.length],
          }))
        );

        setTopClientesData(
          (d_topClientes || []).map((d, index) => ({
            name: d.cliente_corporativo,
            value: d.total,
            color: THEME.colors[index % THEME.colors.length],
          }))
        );

        setJudicialResumen(d_judicialResumen || null);

        const serviceTypes = ['TELEFONICO', 'SITIO', 'CONCILIACION', 'PENAL', 'CIVIL', 'OTROS', 'DEFAULT'];
        const hourlyMap = new Map();
        for(let i=0; i<24; i++) {
          const hObj = { hour: `${i}:00`, total: 0 };
          serviceTypes.forEach(t => hObj[t] = 0);
          hourlyMap.set(i, hObj);
        }
        if (d_asistenciasHora) {
          d_asistenciasHora.forEach(item => {
            const hNum = parseInt(item.hora.split(':')[0]);
            const curr = hourlyMap.get(hNum);
            if (curr) {
              const k = serviceTypes.includes(item.tipo_servicio) ? item.tipo_servicio : 'DEFAULT';
              curr[k] += item.total; curr.total += item.total;
            }
          });
        }
        setAsistenciaHorariaData(Array.from({ length: 24 }, (_, i) => hourlyMap.get(i)));

      } catch (e) { setError(e.message); } finally { setIsLoading(false); }
    };
    const tid = setTimeout(() => fetchDashboardData(), 500);
    return () => clearTimeout(tid);
  }, [filters]);

  const handleGenerateReport = useCallback(async () => {
    setIsGeneratingReport(true);
    const doc = new jsPDF();
    const addImg = async (id, y, h = 80) => {
      const el = document.getElementById(id); if (!el) return y;
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#0f172a' });
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', 15, y, 180, h);
      return y + h + 15;
    };
    try {
      doc.setFontSize(22); doc.text('Reporte LegalPlus', 105, 20, { align: 'center' });
      let y = 40; doc.setFontSize(12);
      cardsData.forEach(c => { doc.text(`${c.title}: ${c.value}`, 20, y); y += 8; });
      y = await addImg('flujo-casos-chart', y + 10);
      y = await addImg('asistencias-tipo-chart', y);
      doc.addPage(); y = await addImg('asistencia-heatmap', 20, 120);
      doc.save(`LegalPlus-Report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (e) {} finally { setIsGeneratingReport(false); }
  }, [cardsData]);
  const isDaily = !!(filters.fechaInicio || filters.fechaFin || (filters.mes && filters.anio));

  const userRole = (user?.rol || user?.role || '').toLowerCase();

  // Contabilidad solo ve el módulo de reportes
  if (userRole === 'contabilidad') {
    return (
      <div className={`h-screen overflow-y-auto ${THEME.bg} p-4 md:p-8 font-sans transition-colors duration-500`}>
        <ReportBuilder onBack={() => {}} />
      </div>
    );
  }

  if (vistaActual === 'reportes') {
    return (
      <div className={`h-screen overflow-y-auto ${THEME.bg} p-4 md:p-8 font-sans transition-colors duration-500`}>
        <ReportBuilder onBack={() => setVistaActual('dashboard')} />
      </div>
    );
  }

  return (
    
    // CAMBIO: h-screen y overflow-y-auto con bg dinámico
    <div className={`h-screen overflow-y-auto ${THEME.bg} p-4 md:p-8 font-sans transition-colors duration-500`}>
      <HeaderModern onGenerateReport={handleGenerateReport} isLoading={isGeneratingReport || isLoading} userRole={user?.rol || user?.role} onMisReportes={() => setVistaActual('reportes')} />
      
      {/* Filtros: bg-gray-800 fijo */}
      <div className="mb-8 p-4 glass-brand rounded-brand-lg">
        <FilterBar filters={filters} setFilters={setFilters} />
      </div>

      {isLoading && (
        <div className="flex flex-col justify-center items-center h-96">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-2 border-brand-indigo/20 border-t-brand-indigo animate-spin" />
            <div className="absolute inset-0 rounded-full bg-brand-indigo/10 blur-xl animate-halo-breathe" />
          </div>
          <p className="mt-5 eyebrow text-brand-silver-mute animate-pulse">Analizando datos legales</p>
        </div>
      )}

      {error && (
        <div className="flex justify-center items-center h-64 text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 rounded-3xl p-6 transition-colors">
          <AlertTriangle className="h-12 w-12 mr-4" />
          <span className="text-xl font-medium">Error: {error}</span>
        </div>
      )}

      {!isLoading && !error && (
        <div className="space-y-6 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cardsData.map((card, idx) => (
              <ModernStatCard key={idx} title={card.title} value={card.value} icon={card.icon} subtext={card.subtext} colorIdx={idx} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2"><ModernAreaChart
              allData={flujoCasosData}
              isDailyView={isDaily}
              windowStart={windowStart}
              onPrev={() => setWindowStart(s => Math.max(0, s - 14))}
              onNext={() => setWindowStart(s => Math.min(flujoCasosData.length - 14, s + 14))}
            /></div>
            <div className="lg:col-span-1"><CaseStatusDonut active={caseMetrics.active} total={caseMetrics.total} /></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
             <div className="lg:col-span-2"><ModernHourlyActivity fullData={asistenciaHorariaData} /></div>
             <div className="lg:col-span-1"><ModernBarChart data={asistenciasPorTipoData} /></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ModernRankingList
              title="Top Abogados"
              icon={Briefcase}
              data={topAbogadosData}
              iconColor="text-violet-500"
            />
            <ModernRankingList
              title="Top Clientes"
              icon={TrendingUp}
              data={topClientesData}
              iconColor="text-emerald-500"
            />
          </div>

          <div className="grid grid-cols-1 gap-6">
            <JudicialSummaryCard data={judicialResumen} />
          </div>
        </div>
      )}
    </div>
  );
}